// src/services/api.js
import {
  getToken,
  getRefreshToken,
  clearAllTokens,
} from './tokenStorage';
import { silentRefresh } from './authRefresh';
import { publishLogout } from './authSync';
import { getOrCreateClientDeviceId } from './deviceId';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

let _detectedClientIp = null;

function detectLocalIp() {
  return new Promise((resolve) => {
    try {
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pc.createDataChannel('');
      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer))
        .catch(() => resolve(null));

      const found = new Set();
      const timeout = setTimeout(() => {
        try { pc.close(); } catch {}
        const ip = [...found].find((ip) => !ip.startsWith('127.') && !ip.startsWith('0.'));
        resolve(ip || null);
      }, 3000);

      pc.onicecandidate = (e) => {
        if (!e || !e.candidate) return;
        const matches = e.candidate.candidate.matchAll(/(\d{1,3}(?:\.\d{1,3}){3})/g);
        for (const m of matches) {
          const ip = m[1];
          if (!ip.startsWith('127.') && !ip.startsWith('0.')) {
            found.add(ip);
            if (/^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(ip)) {
              clearTimeout(timeout);
              try { pc.close(); } catch {}
              resolve(ip);
            }
          }
        }
      };
    } catch {
      resolve(null);
    }
  });
}

detectLocalIp().then((ip) => { if (ip) _detectedClientIp = ip; });

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'X-Client-Device-Id': getOrCreateClientDeviceId(),
    ...(_detectedClientIp ? { 'X-Client-IP': _detectedClientIp } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE_URL}${path}`, { credentials: 'include', ...options, headers });

  if (res.status === 401 && !options._retry && path !== '/auth/refresh' && path !== '/auth/login') {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((newToken) => {
        const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
        return request(path, { ...options, _retry: true, headers: retryHeaders });
      });
    }

    isRefreshing = true;
    try {
      const result = await silentRefresh();
      const newAccess = result.accessToken || getToken();
      if (!newAccess) throw new Error('REFRESH_FAILED');

      processQueue(null, newAccess);
      isRefreshing = false;

      return request(path, {
        ...options,
        _retry: true,
        headers: { ...headers, Authorization: `Bearer ${newAccess}` },
      });
    } catch (err) {
      processQueue(err, null);
      isRefreshing = false;
      clearAllTokens();
      publishLogout();
      window.dispatchEvent(new Event('smartgaz:unauthorized'));
      throw err;
    }
  }

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    let msg = data.reason || data.message || `HTTP ${res.status}`;
    if (Array.isArray(msg)) msg = msg.join('; ');

    if (msg === 'SESSION_TERMINATED') {
      const err = new Error('SESSION_TERMINATED');
      err.code = 'SESSION_TERMINATED';
      throw err;
    }
    throw new Error(msg);
  }

  if (data && data.success === true && data.data !== undefined) {
    return data.data;
  }
  return data;
}

export const authApi = {
  login: (body) => request('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  register: (body) => request('/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  getMe: () => request('/users/me').then(user => ({ user })),
  refresh: async () => {
    const result = await silentRefresh();
    return {
      status: 'SUCCESS',
      accessToken: result.accessToken || getToken(),
      refreshToken: result.refreshToken,
    };
  },
  logout: () => {
    const refreshToken = getRefreshToken();
    return request('/auth/logout', {
      method: 'POST',
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
  },

  sendOtp: (phone) => request('/auth/otp/send', { method: 'POST', body: JSON.stringify({ phone }) }),
  resendOtp: (phone) => request('/auth/otp/resend', { method: 'POST', body: JSON.stringify({ phone }) }),
  verifyOtp: (requestId, code) => request('/auth/otp/verify', { method: 'POST', body: JSON.stringify({ requestId, code }) }),

  sendEmailCode: (email) => request('/auth/email/send-code', { method: 'POST', body: JSON.stringify({ email }) }),
  resendEmailCode: (email) => request('/auth/email/resend-code', { method: 'POST', body: JSON.stringify({ email }) }),
  verifyEmail: (payload) => request('/auth/email/verify', { method: 'POST', body: JSON.stringify(payload) }),

  forgotPassword: (email) => request('/auth/password/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  forgotPasswordByPhone: (phone) => request('/auth/password/forgot-by-phone', { method: 'POST', body: JSON.stringify({ phone }) }),
  resetPassword: (payload) => request('/auth/password/reset', { method: 'POST', body: JSON.stringify(payload) }),
  setupPassword: (password, token) => request('/auth/password/setup', { 
    method: 'POST', 
    body: JSON.stringify({ password }),
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }),

  getSessions: () => request('/auth/sessions'),
  terminateSession: (id) => request(`/auth/sessions/${id}`, { method: 'DELETE' }),
  terminateOtherSessions: (refreshToken) =>
    request('/auth/sessions/other', { method: 'DELETE', body: JSON.stringify({ refreshToken }) }),
};

export const userApi = {
  getProfile: () => request('/users/me'),
  updateProfile: (body) => request('/users/me', { method: 'PATCH', body: JSON.stringify(body) }),
  getAllUsers: () => request('/users'),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),
};

export const orderApi = {
  create: (body) => request('/orders', { method: 'POST', body: JSON.stringify(body) }),
  cancel: (orderId) => request('/orders/cancel', { method: 'POST', body: JSON.stringify({ orderId }) }),
  complete: (orderId) => request('/orders/complete', { method: 'POST', body: JSON.stringify({ orderId }) }),
  getPending: (routeId, mahallaId) => {
    let url = `/orders/pending?routeId=${routeId}`;
    if (mahallaId) url += `&mahallaId=${mahallaId}`;
    return request(url);
  },
  getFleetStatus: () => request('/orders/fleet-status'),
  getQueuePosition: (orderId) => request(`/orders/${orderId}/queue-position`),
  getFullQueue: (routeId) => request(`/orders/route/${routeId}/queue`),
  getMyOrders: () => request('/orders/my'),
  completeByQr: (token, cylinderSerials) =>
    request('/orders/complete-by-qr', { method: 'POST', body: JSON.stringify({ token, cylinderSerials }) }),
  completeByPhone: (phone, cylinderSerials) =>
    request('/orders/complete-by-phone', { method: 'POST', body: JSON.stringify({ phone, cylinderSerials }) }),
};

export const queueApi = {
  getAll: (routeId) => orderApi.getFullQueue(routeId || 'active'),
  getFleetStatus: () => orderApi.getFleetStatus(),
  join: (body) => orderApi.create(body),
  leave: (orderId) => orderApi.cancel(orderId),
};

export const paymentApi = {
  topup: (body) => request('/payments/topup', { method: 'POST', body: JSON.stringify(body) }),
  getTransactions: (userId) => {
    const url = userId ? `/payments/transactions?userId=${userId}` : '/payments/transactions';
    return request(url);
  },
  subscribePremium: () => request('/payments/subscribe-premium', { method: 'POST' }),
};

export const routeApi = {
  getAll: () => request('/routes'),
  create: (body) => request('/routes', { method: 'POST', body: JSON.stringify(body) }),
  activate: (id) => request(`/routes/${id}/activate`, { method: 'PATCH' }),
  complete: (id) => request(`/routes/${id}/complete`, { method: 'PATCH' }),
};

export const logisticsApi = {
  updateLocation: (vehicleId, lat, lng) =>
    request(`/logistics/vehicle/${vehicleId}/location`, { method: 'POST', body: JSON.stringify({ lat, lng }) }),
  getFraudCheck: (vehicleId) => request(`/logistics/vehicle/${vehicleId}/fraud-check`),
  getTracking: (routeId) => request(`/logistics/vehicle/${routeId}/track`),
  getUserActiveRoute: () => request('/orders/active-route'),
};

export const notificationApi = {
  getAll: () => request('/notifications'),
  markAsRead: (id) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
  getUnreadCount: () => request('/notifications/unread/count'),
};

export const adminApi = {
  getStats: () => request('/admin/dashboard/stats'),
  getRecentOrders: () => request('/admin/dashboard/recent-orders'),
  getUsers: () => request('/admin/users'),
  getActiveRoutes: () => request('/admin/routes/active'),
  getAnalytics: () => request('/admin/analytics'),
  blockUser: (id, block) => request(`/admin/users/${id}/block`, { method: 'PATCH', body: JSON.stringify({ block }) }),
  updateUser: (id, data) => request(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  getVehicles: () => request('/admin/vehicles'),
  createVehicle: (data) => request('/admin/vehicles', { method: 'POST', body: JSON.stringify(data) }),
  updateVehicle: (id, data) => request(`/admin/vehicles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteVehicle: (id) => request(`/admin/vehicles/${id}`, { method: 'DELETE' }),

  getMahallas: () => request('/admin/mahallas'),
  getCylinders: () => request('/admin/cylinders'),
  getTransactions: () => request('/admin/transactions'),
};

export const mahallaApi = {
  getAll: () => request('/mahallas'),
  create: (body) => request('/mahallas', { method: 'POST', body: JSON.stringify(body) }),
  search: (q) => {
    const params = q?.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
    return request(`/mahallas/search${params}`);
  },
};

export const driverApi = {
  getQueue: (routeId = 'active') => request(`/orders/pending?routeId=${routeId}`),
  updateStatus: (orderId, status, cylinderSerials = []) =>
    request('/orders/workflow', {
      method: 'PATCH',
      body: JSON.stringify({ orderId, status, cylinderSerials }),
    }),
  updateLocation: (routeId, lat, long) =>
    request('/tracking/ping', {
      method: 'POST',
      body: JSON.stringify({ routeId, lat, long }),
    }),
  activateRoute: (routeId) => request(`/routes/${routeId}/activate`, { method: 'PATCH' }),
  completeRoute: (routeId) => request(`/routes/${routeId}/complete`, { method: 'PATCH' }),
};
